/**
 * Element pickers and forms shared by the design editor's and the video editor's Elements panels:
 * category tiles, charts, tables, graphics, mockups and AI-generated elements.
 */
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import type { FabricObject } from "fabric";
import type { AppDispatch } from "../../../store";
import { generateStudioElement } from "../../../features/studio-controller/StudioServices";
import type { MediaItem } from "../../../types/studioTypes";
import { Icon, IconName } from "../Icon";
import {
  CHART_KINDS,
  ChartKind,
  ChartSpec,
  ELEMENT_COLORS,
  GRAPHICS,
  GraphicDef,
  MOCKUPS,
  MockupKind,
  TABLE_STYLES,
  TableSpec,
  TableStyle,
  chartRowsText,
  makeChart,
  makeMockup,
  makeTable,
  parseChartRows,
  parseTable,
  previewOf,
  renderToBlob,
  tableText,
} from "../elementKit";

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

const ring =
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[rgba(11,122,90,0.45)]";
const BTN = `inline-flex min-h-[38px] w-full cursor-pointer items-center justify-center gap-2 rounded-10 border border-st-green bg-st-green-dark px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${ring}`;
const LABEL = "mb-1.5 mt-3 block text-[13px] font-semibold";
const INPUT = `w-full rounded-[9px] border border-st-line bg-white px-[11px] py-[9px] text-sm text-st-ink ${ring}`;
const HINT = "mt-2 text-[13px] leading-normal text-st-muted";
const ERROR = "mt-2 text-[13px] text-st-red";
const TILE = `group grid aspect-square cursor-pointer place-items-center overflow-hidden rounded-10 border border-st-line bg-st-paper p-1.5 hover:border-st-green ${ring}`;

// ================================================================ categories

export type ElementCategory =
  | "graphics"
  | "photos"
  | "videos"
  | "audio"
  | "ai"
  | "mockups"
  | "charts"
  | "tables";

export const ELEMENT_CATEGORIES: ReadonlyArray<readonly [ElementCategory, string, IconName]> = [
  ["graphics", "Graphics", "shapes"],
  ["photos", "Photos", "photo"],
  ["videos", "Videos", "video"],
  ["audio", "Audio", "music"],
  ["ai", "AI generated", "sparkle"],
  ["mockups", "Mockups", "file"],
  ["charts", "Charts", "grid"],
  ["tables", "Tables", "layers"],
];

export function CategoryTiles({ onPick }: { onPick: (c: ElementCategory) => void }) {
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {ELEMENT_CATEGORIES.map(([key, label, icon]) => (
        <button
          key={key}
          type="button"
          className={cx(
            "flex flex-col items-center gap-1 rounded-xl border border-st-line bg-white px-1 py-2.5 text-[11.5px] font-semibold leading-tight text-st-ink hover:border-st-green hover:bg-st-green-soft",
            ring,
          )}
          onClick={() => onPick(key)}
        >
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-st-green-soft text-st-green-dark">
            <Icon name={icon} size={18} />
          </span>
          {label}
        </button>
      ))}
    </div>
  );
}

export function CategoryHead({ cat, onBack }: { cat: ElementCategory; onBack: () => void }) {
  const label = ELEMENT_CATEGORIES.find((c) => c[0] === cat)?.[1] ?? "";
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <button
        type="button"
        className={cx("grid h-[30px] w-[30px] place-items-center rounded-lg border-0 bg-transparent text-st-muted hover:bg-st-paper hover:text-st-ink", ring)}
        onClick={onBack}
        aria-label="Back to all elements"
      >
        <Icon name="back" size={16} />
      </button>
      <h3 className="text-[15px] font-bold">{label}</h3>
    </div>
  );
}

// ================================================================ previews

function Preview({ build, deps, alt }: { build: () => Promise<Blob>; deps: unknown[]; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    const t = setTimeout(() => {
      build()
        .then((b) => {
          made = URL.createObjectURL(b);
          if (live) setUrl(made);
        })
        .catch(() => undefined);
    }, 150);
    return () => {
      live = false;
      clearTimeout(t);
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return (
    <div className="mt-3 grid min-h-[120px] place-items-center rounded-xl bg-[repeating-conic-gradient(#eef2f0_0_25%,#fff_0_50%)_0_0/14px_14px] p-2">
      {url ? <img src={url} alt={alt} className="max-h-[220px] w-full object-contain" /> : <span className={HINT}>Drawing…</span>}
    </div>
  );
}

function CachedPreview({ cacheKey, build, alt }: { cacheKey: string; build: () => FabricObject | FabricObject[]; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    previewOf(cacheKey, build)
      .then((u) => live && setUrl(u))
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);
  return url ? (
    <img src={url} alt={alt} className="h-full w-full object-contain transition-transform group-hover:scale-105" />
  ) : (
    <span className="block h-full w-full animate-pulse rounded-lg bg-[#edf2ef]" />
  );
}

function Swatches({ colors, value, onChange }: { colors: string[]; value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {colors.map((c) => (
        <button
          key={c}
          type="button"
          className={cx(
            "h-7 w-7 cursor-pointer rounded-full border border-[rgba(16,35,30,0.18)] hover:scale-110",
            value === c && "shadow-[0_0_0_2px_#fff,0_0_0_4px_#0b7a5a]",
          )}
          style={{ background: c }}
          onClick={() => onChange(c)}
          aria-label={`Colour ${c}`}
        />
      ))}
    </div>
  );
}

const palette = (brand?: string[]) => [...new Set([...(brand || []), ...ELEMENT_COLORS])].slice(0, 12);

// ================================================================ charts

interface ChartFormProps {
  initial: ChartSpec;
  brandColors?: string[];
  submitLabel: string;
  onSubmit: (spec: ChartSpec) => void | Promise<void>;
  busy?: boolean;
}

export function ChartForm({ initial, brandColors, submitLabel, onSubmit, busy }: ChartFormProps) {
  const [kind, setKind] = useState<ChartKind>(initial.kind);
  const [title, setTitle] = useState(initial.title);
  const [rows, setRows] = useState(chartRowsText(initial));
  const [main, setMain] = useState(initial.colors[0] || ELEMENT_COLORS[0]);
  const [card, setCard] = useState(initial.card);
  const parsed = useMemo(() => parseChartRows(rows), [rows]);
  const multi = kind === "pie" || kind === "donut";
  const colors = useMemo(() => {
    const p = palette(brandColors);
    return [main, ...p.filter((c) => c !== main)];
  }, [main, brandColors]);
  const spec: ChartSpec = { kind, title, labels: parsed.labels, values: parsed.values, colors, card };

  return (
    <div>
      <div className="grid grid-cols-5 gap-1">
        {CHART_KINDS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={cx(
              "rounded-lg border px-1 py-2 text-xs font-semibold",
              k === kind ? "border-st-green bg-st-green-soft text-st-green-dark" : "border-st-line bg-white text-st-ink",
            )}
            onClick={() => setKind(k)}
          >
            {label}
          </button>
        ))}
      </div>

      <label className={LABEL}>
        Title
        <input className={cx(INPUT, "mt-1.5 font-normal")} value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <label className={LABEL}>
        Data (one per line: label, value)
        <textarea
          className={cx(INPUT, "mt-1.5 min-h-[120px] font-mono text-[13px] font-normal")}
          value={rows}
          onChange={(e) => setRows(e.target.value)}
          spellCheck={false}
        />
      </label>
      {!parsed.values.length && <p className={ERROR}>Add at least one line like “Jan, 12”.</p>}

      <span className={LABEL}>{multi ? "First colour" : "Colour"}</span>
      <Swatches colors={palette(brandColors)} value={main} onChange={setMain} />

      <label className="my-3 flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" className="h-[17px] w-[17px] accent-st-green" checked={card} onChange={(e) => setCard(e.target.checked)} />
        White card behind the chart
      </label>

      <Preview build={() => renderToBlob([makeChart(spec)], 480)} deps={[JSON.stringify(spec)]} alt="Chart preview" />

      <button type="button" className={cx(BTN, "mt-3")} disabled={!parsed.values.length || busy} onClick={() => onSubmit(spec)}>
        <Icon name="plus" size={16} /> {busy ? "Adding…" : submitLabel}
      </button>
    </div>
  );
}

// ================================================================ tables

interface TableFormProps {
  initial: TableSpec;
  brandColors?: string[];
  submitLabel: string;
  onSubmit: (spec: TableSpec) => void | Promise<void>;
  busy?: boolean;
}

export function TableForm({ initial, brandColors, submitLabel, onSubmit, busy }: TableFormProps) {
  const [src, setSrc] = useState(tableText(initial));
  const [header, setHeader] = useState(initial.header);
  const [style, setStyle] = useState<TableStyle>(initial.style);
  const [color, setColor] = useState(initial.color);
  const rows = useMemo(() => parseTable(src), [src]);
  const spec: TableSpec = { rows, header, style, color };

  return (
    <div>
      <label className={LABEL}>
        Cells (one row per line, cells split by commas or tabs — you can paste from a spreadsheet)
        <textarea
          className={cx(INPUT, "mt-1.5 min-h-[130px] font-mono text-[13px] font-normal")}
          value={src}
          onChange={(e) => setSrc(e.target.value)}
          spellCheck={false}
        />
      </label>
      {!rows.length && <p className={ERROR}>Add at least one row.</p>}

      <label className="my-3 flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" className="h-[17px] w-[17px] accent-st-green" checked={header} onChange={(e) => setHeader(e.target.checked)} />
        First row is a header
      </label>

      <span className={LABEL}>Style</span>
      <div className="grid grid-cols-3 gap-1">
        {TABLE_STYLES.map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={cx(
              "rounded-lg border px-1 py-2 text-xs font-semibold",
              k === style ? "border-st-green bg-st-green-soft text-st-green-dark" : "border-st-line bg-white text-st-ink",
            )}
            onClick={() => setStyle(k)}
          >
            {label}
          </button>
        ))}
      </div>
      {style === "brand" && (
        <>
          <span className={LABEL}>Header colour</span>
          <Swatches colors={palette(brandColors)} value={color} onChange={setColor} />
        </>
      )}

      <Preview build={() => renderToBlob([makeTable(spec)], 480)} deps={[JSON.stringify(spec)]} alt="Table preview" />

      <button type="button" className={cx(BTN, "mt-3")} disabled={!rows.length || busy} onClick={() => onSubmit(spec)}>
        <Icon name="plus" size={16} /> {busy ? "Adding…" : submitLabel}
      </button>
    </div>
  );
}

// ================================================================ graphics and mockups

export function GraphicsGrid({ color, onPick, busy }: { color: string; onPick: (g: GraphicDef) => void; busy?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {GRAPHICS.map((g) => (
        <button key={g.key} type="button" className={TILE} title={g.label} aria-label={`Add ${g.label}`} disabled={busy} onClick={() => onPick(g)}>
          <CachedPreview cacheKey={`g:${g.key}:${color}`} build={() => g.make(color)} alt={g.label} />
        </button>
      ))}
    </div>
  );
}

export function MockupGrid({ onPick, busy }: { onPick: (k: MockupKind) => void; busy?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {MOCKUPS.map(([k, label]) => (
        <button key={k} type="button" className={cx(TILE, "relative")} title={label} disabled={busy} onClick={() => onPick(k)}>
          <CachedPreview cacheKey={`m:${k}`} build={() => makeMockup(k, 400, 400)} alt={label} />
          <span className="absolute bottom-1 left-1 right-1 truncate rounded bg-white/85 text-[11px] font-semibold text-st-ink">{label}</span>
        </button>
      ))}
    </div>
  );
}

// ================================================================ AI generated

const AI_STYLES: Array<[string, string]> = [
  ["STICKER", "Sticker"],
  ["FLAT", "Flat"],
  ["3D", "3D"],
  ["WATERCOLOR", "Watercolour"],
  ["LINE", "Line art"],
  ["PHOTO", "Photo"],
];

const IDEAS = ["a gift box with a red ribbon", "a steaming cup of chai", "a smiling delivery scooter", "a diya with a warm flame"];

/** Describe an element; the server draws it with a transparent background and saves it to the Gallery. */
export function AiElementForm({ enabled, onMade }: { enabled: boolean; onMade: (item: MediaItem) => void | Promise<void> }) {
  const dispatch = useDispatch<AppDispatch>();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("STICKER");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<MediaItem[]>([]);

  if (!enabled) {
    return (
      <p className={HINT}>
        AI elements aren't set up on this server yet (it needs an OpenAI key: studio.ai.openai-key).
      </p>
    );
  }

  async function generate() {
    const p = prompt.trim();
    if (p.length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const item = await dispatch(generateStudioElement({ prompt: p, style })).unwrap();
      setMade((list) => [item, ...list]);
      await onMade(item);
    } catch (e) {
      setError(typeof e === "string" ? e : e instanceof Error ? e.message : "The element couldn't be made.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className={LABEL}>
        Describe the element
        <textarea
          className={cx(INPUT, "mt-1.5 min-h-[80px] font-normal")}
          value={prompt}
          maxLength={400}
          placeholder="For example: a gift box with a red ribbon"
          onChange={(e) => setPrompt(e.target.value)}
        />
      </label>
      {!prompt && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {IDEAS.map((i) => (
            <button key={i} type="button" className="rounded-full border border-st-line bg-white px-2.5 py-1 text-xs hover:border-st-green" onClick={() => setPrompt(i)}>
              {i}
            </button>
          ))}
        </div>
      )}
      <span className={LABEL}>Style</span>
      <div className="flex flex-wrap gap-1.5">
        {AI_STYLES.map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={cx(
              "rounded-full border px-3 py-1 text-xs font-semibold",
              k === style ? "border-st-ink bg-st-ink text-white" : "border-st-line bg-white text-st-ink",
            )}
            onClick={() => setStyle(k)}
          >
            {label}
          </button>
        ))}
      </div>
      <button type="button" className={cx(BTN, "mt-3")} disabled={busy || prompt.trim().length < 2} onClick={generate}>
        <Icon name="sparkle" size={16} /> {busy ? "Creating… this can take up to a minute" : "Create element"}
      </button>
      {error && <p className={ERROR} role="alert">{error}</p>}
      <p className={HINT}>Made elements are saved to your Gallery with a transparent background.</p>
      {made.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {made.map((m) => (
            <button key={m.id} type="button" className={TILE} onClick={() => onMade(m)} title="Add again">
              <img src={m.mediaUrl} alt={m.templateName || "AI element"} className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
